import { useEffect, useId, useRef, useState } from 'react'
import { Card } from '@/components/ui/card'
import {
  formatPercent,
  formatWaveDate,
  weekLabel,
  type CalculatedWaveWeek,
} from './model'

const SERIES = [
  {
    key: 'cumulativeWave',
    label: 'Planned waves',
    color: 'var(--color-primary)',
    dash: undefined,
  },
  {
    key: 'skuCumulativeWave',
    label: 'SKU-calculated waves',
    color: '#7c3aed',
    dash: '8 3 2 3',
  },
  {
    key: 'receiptsInStores',
    label: 'Receipts in stores',
    color: '#477b87',
    dash: '6 3',
  },
  {
    key: 'salesInStores',
    label: 'Sales in stores',
    color: '#6b7280',
    dash: '2 4',
  },
] as const

/** About eight evenly spaced axis labels, always including the last week. */
function tickIndexes(count: number): number[] {
  if (count <= 0) return []
  const step = Math.max(1, Math.round((count - 1) / 8))
  const ticks = Array.from(
    { length: Math.floor((count - 1) / step) + 1 },
    (_, index) => index * step
  )
  if (ticks[ticks.length - 1] !== count - 1) {
    // Avoid a cramped last label: replace a tick that sits too close to it.
    if (count - 1 - ticks[ticks.length - 1] < step / 2) ticks.pop()
    ticks.push(count - 1)
  }
  return ticks
}

export function WavePlanningChart({
  weeks,
  setIndex,
  setDate,
}: {
  weeks: CalculatedWaveWeek[]
  /** Timeline position of the set week, or -1. */
  setIndex: number
  /** The plan's set date: the marker's label, not the week's receipt date. */
  setDate: string | null
}) {
  const titleId = useId()
  const [requested, setActive] = useState(Math.max(0, setIndex))
  const active = Math.min(requested, Math.max(0, weeks.length - 1))
  const selected = weeks[active]
  // A series an old frozen version doesn't have (SKU-calculated waves) is left out.
  const series = SERIES.filter(item =>
    weeks.every(week => week[item.key] != null)
  )
  const scroller = useRef<HTMLDivElement>(null)
  const max = Math.max(
    1,
    Math.ceil(
      Math.max(
        0,
        ...weeks.map(week =>
          Math.max(week.cumulativeWave, week.skuCumulativeWave ?? 0)
        )
      ) * 4
    ) / 4
  )
  const left = 52,
    right = 748,
    top = 22,
    bottom = 190
  const x = (index: number) =>
    weeks.length > 1
      ? left + (index / (weeks.length - 1)) * (right - left)
      : (left + right) / 2
  const y = (value: number) => bottom - (value / max) * (bottom - top)

  // Keep the inspected week visible when the chart scrolls (narrow screens).
  useEffect(() => {
    const box = scroller.current
    if (!box || box.scrollWidth <= box.clientWidth) return
    const position = (x(active) / 780) * box.scrollWidth
    if (
      position < box.scrollLeft ||
      position > box.scrollLeft + box.clientWidth
    )
      box.scrollLeft = position - box.clientWidth / 2
    // x only depends on the week count
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, weeks.length])

  if (!selected)
    return (
      <Card className="flex min-w-0 flex-col justify-center p-5 text-sm text-muted-foreground shadow-none">
        No weeks to chart yet.
      </Card>
    )

  return (
    <Card className="flex min-w-0 flex-col justify-between p-5 shadow-none">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 id={titleId} className="text-sm font-semibold">
          Cumulative wave outlook
        </h2>
        <span className="text-xs text-muted-foreground">
          {weekLabel(weeks[0].week)}–{weekLabel(weeks[weeks.length - 1].week)}
        </span>
      </div>
      <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2">
        {series.map(series => (
          <span
            key={series.key}
            className="flex items-center gap-1.5 text-xs text-muted-foreground"
          >
            <svg width="20" height="8" aria-hidden="true">
              <line
                x1="0"
                y1="4"
                x2="20"
                y2="4"
                stroke={series.color}
                strokeWidth="2"
                strokeDasharray={series.dash}
              />
            </svg>
            {series.label}
          </span>
        ))}
      </div>
      <div
        ref={scroller}
        role="slider"
        tabIndex={0}
        aria-label="Inspect wave outlook by week"
        aria-valuemin={1}
        aria-valuemax={weeks.length}
        aria-valuenow={active + 1}
        aria-valuetext={`${weekLabel(selected.week)}: planned waves ${formatPercent(selected.cumulativeWave)}, ${selected.skuCumulativeWave == null ? '' : `SKU-calculated waves ${formatPercent(selected.skuCumulativeWave)}, `}receipts in stores ${formatPercent(selected.receiptsInStores)}, sales in stores ${formatPercent(selected.salesInStores)}`}
        className="mt-2 overflow-x-auto rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
        onKeyDown={event => {
          const next =
            event.key === 'Home'
              ? 0
              : event.key === 'End'
                ? weeks.length - 1
                : event.key === 'ArrowRight' || event.key === 'ArrowUp'
                  ? Math.min(active + 1, weeks.length - 1)
                  : event.key === 'ArrowLeft' || event.key === 'ArrowDown'
                    ? Math.max(0, active - 1)
                    : null
          if (next !== null) {
            event.preventDefault()
            setActive(next)
          }
        }}
      >
        <svg
          viewBox="0 0 780 218"
          className="block w-full min-w-[520px]"
          aria-hidden="true"
          onPointerMove={event => {
            const bounds = event.currentTarget.getBoundingClientRect()
            const position =
              ((event.clientX - bounds.left) / bounds.width) * 780
            setActive(
              Math.max(
                0,
                Math.min(
                  weeks.length - 1,
                  Math.round(
                    ((position - left) / (right - left)) * (weeks.length - 1)
                  )
                )
              )
            )
          }}
        >
          {Array.from({ length: 5 }, (_, index) => (max * index) / 4).map(
            value => (
              <g key={value}>
                <line
                  x1={left}
                  x2={right}
                  y1={y(value)}
                  y2={y(value)}
                  stroke="var(--color-border)"
                  strokeDasharray={value === 0 ? undefined : '3 4'}
                />
                <text
                  x={left - 9}
                  y={y(value) + 4}
                  textAnchor="end"
                  fontSize="11"
                  fill="var(--color-muted-foreground)"
                >
                  {formatPercent(value, 0)}
                </text>
              </g>
            )
          )}
          {tickIndexes(weeks.length).map(index => (
            <text
              key={index}
              x={x(index)}
              y="210"
              textAnchor="middle"
              fontSize="10"
              fill="var(--color-muted-foreground)"
            >
              {weekLabel(weeks[index].week)}
            </text>
          ))}
          {setIndex >= 0 && (
            <g>
              <line
                x1={x(setIndex)}
                x2={x(setIndex)}
                y1={top}
                y2={bottom}
                stroke="var(--color-primary)"
                opacity="0.35"
                strokeDasharray="4 4"
              />
              <text
                x={x(setIndex)}
                y="12"
                textAnchor="middle"
                fontSize="10"
                fill="var(--color-primary)"
              >
                Set · {formatWaveDate(setDate)}
              </text>
            </g>
          )}
          {series.map(series => (
            <path
              key={series.key}
              data-series={series.key}
              d={weeks
                .map((week, index) =>
                  index === 0
                    ? `M${x(index)},${y(week[series.key] ?? 0)}`
                    : series.key === 'cumulativeWave' ||
                        series.key === 'skuCumulativeWave'
                      ? `H${x(index)}V${y(week[series.key] ?? 0)}`
                      : `L${x(index)},${y(week[series.key] ?? 0)}`
                )
                .join(' ')}
              fill="none"
              stroke={series.color}
              strokeWidth="2.5"
              strokeDasharray={series.dash}
              strokeLinejoin="round"
            />
          ))}
          <line
            x1={x(active)}
            x2={x(active)}
            y1={top}
            y2={bottom}
            stroke="var(--color-muted-foreground)"
            opacity="0.4"
          />
          {series.map(series => (
            <circle
              key={series.key}
              cx={x(active)}
              cy={y(selected[series.key] ?? 0)}
              r="3.5"
              fill={series.color}
              stroke="white"
              strokeWidth="1.5"
            />
          ))}
        </svg>
      </div>
      <div
        className="mt-2 flex flex-wrap gap-x-4 gap-y-1 rounded-md bg-secondary/70 px-3 py-2 text-xs tabular-nums"
        data-testid="wave-chart-readout"
      >
        <span className="font-semibold">
          {weekLabel(selected.week)} · {formatWaveDate(selected.date)}
        </span>
        {series.map(series => (
          <span key={series.key} className="text-muted-foreground">
            {series.label}{' '}
            <strong className="font-semibold text-foreground">
              {formatPercent(selected[series.key] ?? 0)}
            </strong>
          </span>
        ))}
      </div>
      <p className="mt-2 text-[11px] text-muted-foreground">
        Hover to inspect · Use arrow keys when the chart is focused
      </p>
    </Card>
  )
}
